import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { IUser } from '../../atoms';
import { apiGet } from '../../services/_common';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';

// D-DAY
export const Dday = ({ name, userInfo, refetchInterval }: { name: string; userInfo: IUser; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;
  const { data, isLoading, isError, refetch } = useQuery(['siteInfoGet', userInfo], () => fetchData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
  });

  const fetchData = async () => {
    try {
      const res = await apiGet({ path: '/site/info', req: { hCd, sCd } });
      const { siteInfo } = res.data.data;
      const today = dayjs().format('YYYY-MM-DD');
      const today2 = dayjs(today);
      const nStartDate = dayjs(siteInfo.sDate);
      const nFinishDate = dayjs(siteInfo.fDate);

      if (!nStartDate.isValid() || !nFinishDate.isValid()) {
        return { startDate: '', finishDate: '', processPercentage: 0, processDays: 0, leftDays: 0 };
      }
      const nprocessDays = today2.diff(nStartDate, 'day');
      const nleftDays = nFinishDate.diff(today2, 'day') >= 0 ? nFinishDate.diff(today2, 'day') : 0;
      const totalDays = nFinishDate.diff(nStartDate, 'day');
      const percentage = Math.round((nprocessDays / totalDays) * 100) <= 100 ? Math.round((nprocessDays / totalDays) * 100) : 100;

      return { startDate: siteInfo.sDate, finishDate: siteInfo.fDate, processPercentage: percentage, processDays: nprocessDays, leftDays: nleftDays };
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickRefresh = () => {
    refetch();
  };

  const loading = !data || isLoading;
  if (isError) return <IssueGuide isImg={false} />;

  return (
    <div className='dDay'>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t(name)}</span>
          {!isSiteDashboard && (
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <SkeletonTable rows={1} />
      ) : (
        <div className='widget-body progressBar'>
          <div className='content'>
            <dl className='flex-basic'>
              <dt>{t('전체 기간')}</dt>
              {!data?.startDate || !data?.finishDate ? (
                <span className='badge'>{t('시공 일자 설정 필요')}</span>
              ) : (
                <dd>
                  {data.startDate} ~ {data.finishDate}
                </dd>
              )}
            </dl>
            <div className='flex-basic'>
              <dl className='flex-basic'>
                <dt>{t('진행 일 수')}</dt>
                <dd>
                  <span className='green'>{data.processDays}</span> {t('-일')}
                </dd>
              </dl>
              <span />
              <dl className='flex-basic'>
                <dt>{t('남은 일 수')}</dt>
                <dd>
                  <span className='blue'>{data.leftDays}</span> {t('-일')}
                </dd>
              </dl>
            </div>
          </div>
          <div className='progressBar-group flex-col'>
            <div className='progressBar-text flex-between'>
              <span>{t('전체 진행률')}</span>
              <span>{data.processPercentage}%</span>
            </div>
            <div className='fullBar'>
              <div className='fillBar' style={{ width: `${data.processPercentage}%` }} />
              <div className='emptyBar' style={{ width: `${100 - data.processPercentage}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
